<template>
  <Modal
    v-model="visible"
    :title="isEdit ? $translate('editMulticastConfig') : $translate('addMulticastConfig')"
    :width="800"
    :mask-closable="false"
    class="multicast-form-modal"
  >
    <div class="form-container">
      <Form ref="multicastForm" :model="formData" :rules="formRules" :label-width="120">
        <!-- 配置名称 -->
        <FormItem :label="$translate('configurationName')" prop="name">
          <Input
            v-model="formData.name"
            :placeholder="$translate('pleaseEnterConfigName')"
            class="tech-input"
          />
        </FormItem>

        <!-- 动态生成的表单项 -->
        <FormItem
          v-for="field in configFields"
          :key="field.key"
          :label="field.label"
          :prop="field.key"
        >
          <!-- 输入框类型 -->
          <Input
            v-if="field.type === 'input'"
            v-model="formData[field.key]"
            :placeholder="field.placeholder"
            class="tech-input"
          />

          <!-- 数字输入框类型 -->
          <InputNumber
            v-else-if="field.type === 'number'"
            v-model="formData[field.key]"
            :placeholder="field.placeholder"
            :min="field.min"
            :max="field.max"
            class="tech-input"
            style="width: 100%"
          />

          <!-- 下拉选择框类型 -->
          <Select
            v-else-if="field.type === 'select'"
            v-model="formData[field.key]"
            :placeholder="field.placeholder"
            class="tech-input"
          >
            <Option
              v-for="option in field.options"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </Option>
          </Select>

          <!-- 开关类型 -->
          <i-switch
            v-else-if="field.type === 'switch'"
            v-model="formData[field.key]"
            class="tech-switch"
          />
        </FormItem>

        <!-- 是否启用 -->
        <FormItem :label="$translate('whetherToEnable')" prop="enabled">
          <div class="enable-switch-container">
            <span
              class="switch-label"
              :class="{ 'enabled': formData.enabled === 1, 'disabled': formData.enabled === 2 }"
            >
              <span class="status-icon">●</span>
              {{ formData.enabled === 1 ? $translate('enabled') : $translate('disabled') }}
            </span>
            <i-switch
              :value="formData.enabled === 1"
              @on-change="handleEnabledChange"
              size="large"
              class="tech-switch"
            />
          </div>
        </FormItem>

        <!-- 机器人选择 -->
        <FormItem :label="$translate('associatedRobots')">
          <div class="robot-selector-container">
            <Button
              type="dashed"
              @click="showRobotSelector = true"
              class="select-robot-btn"
            >
              <svg-icon name="robot" class="btn-icon"></svg-icon>
              {{$translate('selectRobots')}} ({{ selectedRobots.length }})
            </Button>

            <div v-if="selectedRobots.length > 0" class="selected-robots">
              <Tag
                v-for="robot in selectedRobots"
                :key="robot.id"
                closable
                @on-close="removeRobot(robot.id)"
                class="robot-tag"
              >
                {{ robot.name }}
              </Tag>
            </div>
          </div>
        </FormItem>
      </Form>
    </div>

    <div slot="footer">
      <Button
        type="primary"
        @click="handleSubmit"
        :loading="submitting"
        class="submit-btn"
      >
        <svg-icon name="save" class="btn-icon"></svg-icon>
        {{ isEdit ? $translate('update') : $translate('save') }}
      </Button>
      <Button @click="handleCancel" class="cancel-btn">{{$translate('cancel')}}</Button>
    </div>

    <!-- 机器人选择器 -->
    <RobotSelector
      v-model="showRobotSelector"
      :selectedIds="formData.robotIds"
      @confirm="handleRobotSelect"
    />
  </Modal>
</template>

<script>
import RobotSelector from '@/components/RobotSelector'
import api from '@/api'

export default {
  name: 'MulticastForm',
  components: {
    RobotSelector
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    editData: {
      type: Object,
      default: null
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      submitting: false,
      showRobotSelector: false,
      selectedRobots: [],
      configFields: [],
      formData: {
        id: null,
        name: '',
        enabled: 1,
        robotIds: []
      },
      formRules: {
        name: []
      }
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.visible = newVal
        if (newVal) {
          this.initForm()
        }
      },
      immediate: true
    },
    visible(newVal) {
      this.$emit('input', newVal)
    }
  },
  mounted() {
    // 初始化基础表单规则
    this.initBaseFormRules()
  },
  methods: {
    // 初始化基础表单规则
    initBaseFormRules() {
      this.formRules.name = [
        { required: true, message: this.$translate('pleaseEnterConfigName'), trigger: 'blur' },
        { min: 1, max: 50, message: this.$translate('configNameLengthRange'), trigger: 'blur' }
      ]
    },

    // 初始化表单
    initForm() {
      this.loadConfigFields().then(() => {
        this.resetForm()
        if (this.isEdit && this.editData) {
          this.loadFormData()
        }
      })
    },

    // 加载配置字段
    loadConfigFields() {
      return new Promise((resolve) => {
        api.getData('system-server/config/list?keys=multicastConfigList').then(response => {
          if (response.state === 0) {
            console.error(this.$translate('loadConfigFieldsFailed') + ':', response.msg)
          } else if (response.data) {
            const config = response.data['multicastConfigList']
            if (config && config.configValue) {
              this.configFields = JSON.parse(config.configValue)
              this.setDynamicRules()
              this.initFormData()
            }
          }
          resolve()
        })
      })
    },

    // 初始化表单数据
    initFormData() {
      this.configFields.forEach(field => {
        if (!this.formData.hasOwnProperty(field.key)) {
          this.$set(this.formData, field.key, field.defaultValue || null)
        }
      })
    },

    // 设置动态验证规则
    setDynamicRules() {
      this.configFields.forEach(field => {
        const rules = []

        // 必填验证
        if (field.required) {
          if (field.type === 'number') {
            // 数字类型的必填验证
            rules.push({
              validator: (rule, value, callback) => {
                if (value === null || value === undefined || value === '') {
                  callback(new Error(field.requiredMessage || `${this.$translate('pleaseEnter')}${field.label}`))
                  return
                }
                callback()
              },
              trigger: 'blur'
            })
          } else {
            // 其他类型的必填验证
            rules.push({
              required: true,
              message: field.requiredMessage || `${this.$translate('pleaseEnter')}${field.label}`,
              trigger: 'blur'
            })
          }
        }

        // 根据验证类型自动生成规则
        if (field.validateType) {
          const validationRules = this.getValidationRules(field.validateType, field)
          rules.push(...validationRules)
        }

        // 自定义规则（保留原有功能）
        if (field.rules) {
          rules.push(...field.rules)
        }

        if (rules.length > 0) {
          this.$set(this.formRules, field.key, rules)
        }
      })
    },

    // 根据验证类型获取验证规则
    getValidationRules(validateType, field) {
      const rules = []

      switch (validateType) {
        case 'ip':
          rules.push({
            pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
            message: this.$translate('pleaseEnterCorrectIPFormat'),
            trigger: 'blur'
          })
          break

        case 'port':
          rules.push({
            validator: (rule, value, callback) => {
              if (value === null || value === undefined || value === '') {
                callback()
                return
              }
              const numValue = Number(value)
              if (isNaN(numValue)) {
                callback(new Error(this.$translate('pleaseEnterValidPort')))
                return
              }
              const min = field.min || 1
              const max = field.max || 65535
              if (numValue < min || numValue > max) {
                callback(new Error(`${this.$translate('portRangeIs')}${min}-${max}`))
                return
              }
              callback()
            },
            trigger: 'blur'
          })
          break

        case 'email':
          rules.push({
            type: 'email',
            message: '请输入正确的邮箱格式',
            trigger: 'blur'
          })
          break

        case 'phone':
          rules.push({
            pattern: /^1[3-9]\d{9}$/,
            message: '请输入正确的手机号码',
            trigger: 'blur'
          })
          break

        case 'url':
          rules.push({
            type: 'url',
            message: '请输入正确的URL格式',
            trigger: 'blur'
          })
          break

        case 'number':
          rules.push({
            validator: (rule, value, callback) => {
              if (value === null || value === undefined || value === '') {
                callback()
                return
              }
              const numValue = Number(value)
              if (isNaN(numValue)) {
                callback(new Error(this.$translate('pleaseEnterValidNumber')))
                return
              }
              if (field.min !== undefined && numValue < field.min) {
                callback(new Error(`${this.$translate('numberCannotBeLessThan')}${field.min}`))
                return
              }
              if (field.max !== undefined && numValue > field.max) {
                callback(new Error(`${this.$translate('numberCannotBeGreaterThan')}${field.max}`))
                return
              }
              callback()
            },
            trigger: 'blur'
          })
          break

        case 'length':
          rules.push({
            min: field.minLength || 0,
            max: field.maxLength || 255,
            message: `${this.$translate('lengthShouldBeBetween')}${field.minLength || 0}-${field.maxLength || 255}${this.$translate('charactersRange')}`,
            trigger: 'blur'
          })
          break

        case 'chinese':
          rules.push({
            pattern: /^[\u4e00-\u9fa5]+$/,
            message: this.$translate('onlyChineseCharacters'),
            trigger: 'blur'
          })
          break

        case 'english':
          rules.push({
            pattern: /^[a-zA-Z]+$/,
            message: this.$translate('onlyEnglishCharacters'),
            trigger: 'blur'
          })
          break

        case 'alphanumeric':
          rules.push({
            pattern: /^[a-zA-Z0-9]+$/,
            message: this.$translate('onlyLettersAndNumbers'),
            trigger: 'blur'
          })
          break

        case 'noSpecialChars':
          rules.push({
            pattern: /^[a-zA-Z0-9\u4e00-\u9fa5]+$/,
            message: this.$translate('noSpecialCharacters'),
            trigger: 'blur'
          })
          break

        default:
          console.warn(`${this.$translate('unknownValidationType')}: ${validateType}`)
      }

      return rules
    },

    // 重置表单
    resetForm() {
      this.formData = {
        id: null,
        name: '',
        enabled: 1,
        robotIds: []
      }
      this.selectedRobots = []
      this.initFormData()
      this.$nextTick(() => {
        this.$refs.multicastForm && this.$refs.multicastForm.resetFields()
      })
    },

    // 加载表单数据
    loadFormData() {
      api.getData(`robot-server/robot/multicast/get/${this.editData.id}`).then(response => {
        if (response.state === 0) {
          this.$Notice.error({
            title: this.$translate('error'),
            desc: response.msg || this.$translate('getConfigInfoFailed')
          })
        } else {
          this.formData = { ...this.formData, ...response.data }
          // 加载关联的机器人信息
          const selectedRobots = response.data.robots || []
          this.handleRobotSelect(selectedRobots)
        }
      })
    },
    // 处理机器人选择
    handleRobotSelect(selectedRobots) {
      this.selectedRobots = selectedRobots
      this.formData.robotIds = selectedRobots.map(robot => robot.id)
    },

    // 移除机器人
    removeRobot(robotId) {
      this.selectedRobots = this.selectedRobots.filter(robot => robot.id !== robotId)
      this.formData.robotIds = this.formData.robotIds.filter(id => id !== robotId)
    },

    // 提交表单
    handleSubmit() {
      this.$refs.multicastForm.validate((valid) => {
        if (valid) {
          this.submitting = true

          api.postData('robot-server/robot/multicast/save', this.formData).then(response => {
            this.submitting = false
            console.log(response)
            if (response.state === 0) {
              this.$Notice.error({
                title: this.$translate('error'),
                desc: response.message || this.$translate('saveFailed')
              })
            } else {
              this.$Notice.success({
                title: this.$translate('success'),
                desc: this.isEdit ? this.$translate('updateSuccessful') : this.$translate('saveSuccessful')
              })
              this.$emit('success')
              this.visible = false
            }
          })
        }
      })
    },

    // 取消
    handleCancel() {
      this.visible = false
    },

    // 处理启用状态变化
    handleEnabledChange(value) {
      this.formData.enabled = value ? 1 : 2
    }
  }
}
</script>

<style lang="less" scoped>
.form-container {
  max-height: 500px;
  overflow-y: auto;
  padding-right: 10px;
}

.tech-input {
  background: rgba(15, 35, 70, 0.7) !important;
  border: 1px solid rgba(0, 191, 255, 0.4) !important;
  border-radius: 8px !important;
  color: #e8f4fd !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 0 5px rgba(0, 191, 255, 0.1) !important;
}

.tech-input:focus {
  border-color: rgba(0, 191, 255, 0.8) !important;
  box-shadow: 0 0 15px rgba(0, 191, 255, 0.3), inset 0 0 5px rgba(0, 191, 255, 0.1) !important;
  background: rgba(15, 35, 70, 0.8) !important;
}

.tech-input:hover {
  border-color: rgba(0, 191, 255, 0.6) !important;
  box-shadow: 0 0 8px rgba(0, 191, 255, 0.15) !important;
}

.tech-radio-group {
  display: flex;
  gap: 20px;
}

.tech-radio {
  color: #e8f4fd !important;
}

/deep/ .tech-switch.ivu-switch {
  background: rgba(15, 35, 70, 0.6) !important;
  border: 1px solid rgba(0, 191, 255, 0.3) !important;
  transition: all 0.3s ease !important;
}

/deep/ .tech-switch.ivu-switch:hover {
  border-color: rgba(0, 191, 255, 0.5) !important;
}

/deep/ .tech-switch.ivu-switch-checked {
  background: linear-gradient(135deg, #00bfff, #0080ff) !important;
  box-shadow: 0 0 10px rgba(0, 191, 255, 0.3) !important;
  border-color: rgba(0, 191, 255, 0.8) !important;
}

/deep/ .tech-switch .ivu-switch-inner {
  background: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
}

.enable-switch-container {
  display: flex;
  align-items: center;
  gap: 15px;
}

.switch-label {
  font-size: 12px;
  font-weight: 600;
  min-width: 50px;
  padding: 4px 10px;
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  gap: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.switch-label.enabled {
  color: #ffffff;
  background: linear-gradient(135deg, #00ff88, #00cc6a);
  border-color: #00ff88;
  box-shadow:
    0 0 8px rgba(0, 255, 136, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
}

.switch-label.disabled {
  color: #ffffff;
  background: linear-gradient(135deg, #ff4757, #ff3742);
  border-color: #ff4757;
  box-shadow:
    0 0 8px rgba(255, 71, 87, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
}

.switch-label .status-icon {
  font-size: 8px;
  animation: pulse 2s infinite;
}

.switch-label.enabled .status-icon {
  color: #ffffff;
  text-shadow: 0 0 5px #00ff88;
}

.switch-label.disabled .status-icon {
  color: #ffffff;
  text-shadow: 0 0 5px #ff4757;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.1);
  }
}

.robot-selector-container {
  display: flex;
  flex-direction: column;
}

.select-robot-btn {
  background: rgba(15, 35, 70, 0.6);
  border: 1px dashed rgba(0, 191, 255, 0.4);
  color: #00bfff;
  border-radius: 8px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  width: fit-content;
}

.select-robot-btn:hover {
  border-color: rgba(0, 191, 255, 0.6);
  background: rgba(0, 191, 255, 0.1);
}

.btn-icon {
  font-size: 14px;
}

.selected-robots {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
  padding: 12px;
  background: rgba(15, 35, 70, 0.4);
  border: 1px solid rgba(0, 191, 255, 0.2);
  border-radius: 8px;
  align-items: flex-start;
  align-content: flex-start;
}

.robot-tag {
  background: linear-gradient(135deg, rgba(0, 191, 255, 0.2), rgba(0, 191, 255, 0.1));
  color: #ffffff;
  border: 1px solid rgba(0, 191, 255, 0.5);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  box-shadow: 0 2px 4px rgba(0, 191, 255, 0.1);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.robot-tag:hover {
  background: linear-gradient(135deg, rgba(0, 191, 255, 0.3), rgba(0, 191, 255, 0.2));
  border-color: rgba(0, 191, 255, 0.7);
  box-shadow: 0 4px 8px rgba(0, 191, 255, 0.2);
  transform: translateY(-1px);
}



.cancel-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #e8f4fd;
  border-radius: 8px;
  padding: 10px 20px;
  margin-left: 10px;
  display: inline-block;
}

.submit-btn {
  background: linear-gradient(135deg, #00bfff, #0080ff);
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(0, 191, 255, 0.3);
  transition: all 0.3s ease;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 191, 255, 0.4);
}

/* 滚动条样式 */
.form-container::-webkit-scrollbar {
  width: 6px;
}

.form-container::-webkit-scrollbar-track {
  background: rgba(0, 191, 255, 0.1);
  border-radius: 3px;
}

.form-container::-webkit-scrollbar-thumb {
  background: rgba(0, 191, 255, 0.3);
  border-radius: 3px;
  transition: all 0.3s ease;
}

.form-container::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 191, 255, 0.5);
}
</style>

<style lang="less">
.multicast-form-modal {
  .ivu-modal-header {
    background: linear-gradient(135deg, #00bfff, #0080ff);
    color: #fff;
    border-bottom: none;
  }

  .ivu-modal-header-inner {
    color: #fff;
    font-weight: 600;
  }

  .ivu-modal-close {
    color: #fff;
  }

  .ivu-modal-close:hover {
    color: rgba(255, 255, 255, 0.8);
  }

  .ivu-modal-body {
    background: linear-gradient(135deg,
      rgba(15, 30, 60, 0.95) 0%,
      rgba(25, 45, 80, 0.9) 50%,
      rgba(10, 25, 50, 0.95) 100%);
    color: #e8f4fd;
    position: relative;
  }

  .ivu-modal-body::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 30%, rgba(0, 191, 255, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(0, 191, 255, 0.06) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }

  .ivu-modal-body > * {
    position: relative;
    z-index: 1;
  }

  .ivu-modal-footer {
    background: linear-gradient(135deg,
      rgba(15, 30, 60, 0.95) 0%,
      rgba(25, 45, 80, 0.9) 50%,
      rgba(10, 25, 50, 0.95) 100%);
    border-top: 1px solid rgba(0, 191, 255, 0.3);
  }

  // 表单标签样式
  .ivu-form-item-label {
    color: #e8f4fd !important;
    font-weight: 500 !important;
  }

  // 下拉选择框样式
  .ivu-select-selection {
    background: rgba(15, 35, 70, 0.7) !important;
    border: 1px solid rgba(0, 191, 255, 0.4) !important;
    color: #e8f4fd !important;
    box-shadow: 0 0 5px rgba(0, 191, 255, 0.1) !important;
  }

  .ivu-select-selection:hover {
    border-color: rgba(0, 191, 255, 0.6) !important;
    box-shadow: 0 0 8px rgba(0, 191, 255, 0.15) !important;
  }

  .ivu-select-focused .ivu-select-selection {
    border-color: rgba(0, 191, 255, 0.8) !important;
    box-shadow: 0 0 15px rgba(0, 191, 255, 0.3) !important;
  }

  // 数字输入框样式
  .ivu-input-number {
    background: rgba(15, 35, 70, 0.7) !important;
    border: 1px solid rgba(0, 191, 255, 0.4) !important;
    color: #e8f4fd !important;
    box-shadow: 0 0 5px rgba(0, 191, 255, 0.1) !important;
  }

  .ivu-input-number:hover {
    border-color: rgba(0, 191, 255, 0.6) !important;
    box-shadow: 0 0 8px rgba(0, 191, 255, 0.15) !important;
  }

  .ivu-input-number-focused {
    border-color: rgba(0, 191, 255, 0.8) !important;
    box-shadow: 0 0 15px rgba(0, 191, 255, 0.3) !important;
  }

  // 开关样式增强
  /deep/ .ivu-switch-checked {
    background: linear-gradient(135deg, #00bfff, #0080ff) !important;
    box-shadow: 0 0 10px rgba(0, 191, 255, 0.3) !important;
  }

  /deep/ .ivu-switch {
    background: rgba(15, 35, 70, 0.6) !important;
    border: 1px solid rgba(0, 191, 255, 0.3) !important;
  }

  // 机器人标签样式增强
  .robot-tag.ivu-tag {
    background: linear-gradient(135deg, rgba(0, 191, 255, 0.2), rgba(0, 191, 255, 0.1)) !important;
    color: #ffffff !important;
    border: 1px solid rgba(0, 191, 255, 0.5) !important;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3) !important;
  }

  .robot-tag.ivu-tag .ivu-tag-text {
    color: #ffffff !important;
    font-weight: 500 !important;
  }

  .robot-tag.ivu-tag .ivu-icon-ios-close {
    color: #ffffff !important;
    font-size: 14px !important;
    margin-left: 4px !important;
    transition: all 0.3s ease !important;
  }

  .robot-tag.ivu-tag .ivu-icon-ios-close:hover {
    color: #ff6b7a !important;
    transform: scale(1.1) !important;
  }

  .robot-tag.ivu-tag:hover {
    background: linear-gradient(135deg, rgba(0, 191, 255, 0.3), rgba(0, 191, 255, 0.2)) !important;
    border-color: rgba(0, 191, 255, 0.7) !important;
    box-shadow: 0 4px 8px rgba(0, 191, 255, 0.2) !important;
    transform: translateY(-1px) !important;
  }
}
</style>
